import React, { useCallback, useMemo } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { useTranslation } from 'react-i18next';
import { useCurrentDecedentId } from '@/app/providers/DecedentProvider';
import { useLocalizedField } from '@/common/hooks';
import { FinInstitutionsForm } from '@/common/containers/Forms';
import { Notification } from '@/common/utils';
import { useSubmitAnswersMutation } from '@/entities/question';
import styles from '@/modules/financials/components/FinancialInstitutionsModal.styles';

const FINANCIAL_DATA_ENTRY_FORM_ID = 'financial-data-entry-form';

const FinancialSectionDataEntryModal = ({
  data,
  isAddMode = false,
  onClose,
  refreshTask,
  title,
}) => {
  const { t } = useTranslation();
  const getLocalizedField = useLocalizedField();
  const decedentId = useCurrentDecedentId();
  const submitAnswersMutation = useSubmitAnswersMutation();
  const loading = submitAnswersMutation.isPending;

  const form = useMemo(() => {
    if (!data) {
      return null;
    }

    return {
      question: data?.props,
      answers: (data?.list || []).map((item) => ({
        ...item,
        status: item.status || 'active',
      })),
      taskStepId: data?.taskStepId,
    };
  }, [data]);

  const closeModal = useCallback(() => {
    if (loading) {
      return;
    }

    onClose?.();
  }, [loading, onClose]);

  const onSubmit = useCallback(
    async (formPayload) => {
      const { question, taskStepId } = form || {};

      if (!formPayload.length) {
        Notification.info(t('No item added. Please add entry'));
        return;
      }

      const payload = formPayload.map((answer) => ({
        ...answer,
        taskStepId,
      }));

      try {
        await submitAnswersMutation.mutateAsync({
          answers: payload,
          decedentId,
          section: 'main',
          sectionType: 'Task',
        });

        Notification.success(
          t('{{item}} is saved', {
            item: getLocalizedField(question, 'listItemName'),
          })
        );
        onClose?.();

        try {
          await refreshTask?.();
        } catch {
          // Save already succeeded; don't show a save error for refresh failure.
        }
      } catch {
        Notification.error(
          t('{{item}} was not saved', {
            item: getLocalizedField(question, 'listItemName'),
          })
        );
      }
    },
    [
      decedentId,
      form,
      getLocalizedField,
      onClose,
      refreshTask,
      submitAnswersMutation,
      t,
    ]
  );

  if (!form) {
    return null;
  }

  const { question, answers } = form;
  const itemName = getLocalizedField(question, 'header');
  const modalTitle =
    title ||
    (isAddMode
      ? t('Add {{item}}', {
          item: itemName,
        })
      : itemName);

  return (
    <Dialog open onClose={closeModal} fullWidth>
      <DialogTitle>{modalTitle}</DialogTitle>
      <DialogContent>
        <FinInstitutionsForm
          question={question}
          answers={answers}
          formId={FINANCIAL_DATA_ENTRY_FORM_ID}
          onSubmit={onSubmit}
        />
      </DialogContent>
      <DialogActions sx={styles.actions}>
        <Button
          type="button"
          variant="outlined"
          color="secondary"
          size="medium"
          onClick={closeModal}
          disabled={loading}
        >
          {t('Cancel')}
        </Button>
        <Button
          type="submit"
          form={FINANCIAL_DATA_ENTRY_FORM_ID}
          variant="contained"
          color="success"
          size="medium"
          loading={loading}
        >
          {t('Save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FinancialSectionDataEntryModal;
